import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { AddressInput } from '../ui/AddressInput';
import { PlaceMap } from '../maps/PlaceMap';
import { PlaceFinder } from './PlaceFinder';
import { Spinner } from '../design-system';
import {
  NOTES_MAX,
  NOTES_MIN,
  describePlace,
  loadStyleExamples,
  pickPatch,
  type CategoryOption,
  type FoundPlace,
  type PlaceFieldsValue,
  type SuggestedField,
} from '../../lib/placePicker';

interface PlaceDetailsEditorProps {
  value: PlaceFieldsValue;
  onChange: (patch: Partial<PlaceFieldsValue>) => void;
  categories: CategoryOption[];
  /** The photo gallery field, which each editor wires to its own storage. */
  photos: ReactNode;
  /** Anything extra under the tip (e.g. "Mark as a highlight"). */
  extra?: ReactNode;
}

const inputClass =
  'w-full rounded-lg border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none';

function Tag({ show, text = 'suggested' }: { show: boolean; text?: string }) {
  if (!show) return null;
  return <span className="text-[11px] font-medium text-gold bg-gold/15 rounded-full px-2 py-0.5">{text}</span>;
}

/**
 * The whole "place" form, shared by the Event Library and the itinerary's Add
 * event: find the place by name (we fill the name, address, map pin and
 * category), add photos, and describe it. For the description the host types a
 * few words about what they love and presses "Write it for me": we turn them
 * into two or three sentences in the host's own style, which they can edit or
 * undo. Nothing is written until they press the button.
 */
export function PlaceDetailsEditor({ value, onChange, categories, photos, extra }: PlaceDetailsEditorProps) {
  const { user, profile } = useAuth();
  const near = profile?.latitude != null && profile?.longitude != null
    ? { lat: Number(profile.latitude), lng: Number(profile.longitude) }
    : null;

  const hasPin = value.latitude != null && value.longitude != null;
  const [manual, setManual] = useState(!hasPin && !!value.address);
  const [changing, setChanging] = useState(false);
  const [suggested, setSuggested] = useState<Set<SuggestedField>>(new Set());
  const [lastPlace, setLastPlace] = useState<FoundPlace | null>(null);

  // "Write it for me"
  const [notes, setNotes] = useState('');
  const [writing, setWriting] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ text: string } | null>(null);

  // The newest values, for when an answer arrives a moment after the click.
  const latest = useRef(value);
  latest.current = value;
  const run = useRef(0);
  useEffect(() => () => { run.current++; }, []);

  function mark(fields: SuggestedField[]) {
    if (fields.length) setSuggested((s) => new Set([...s, ...fields]));
  }
  function edit(field: SuggestedField, patch: Partial<PlaceFieldsValue>) {
    setSuggested((s) => { const n = new Set(s); n.delete(field); return n; });
    if (field === 'description') setUndo(null);
    onChange(patch);
  }

  function pick(place: FoundPlace) {
    const { patch, suggested: fields } = pickPatch(latest.current, place, categories);
    onChange(patch);
    mark(fields);
    setManual(false);
    setChanging(false);
    setLastPlace(place);
  }

  const notesOk = notes.trim().length >= NOTES_MIN && value.name.trim().length > 0;

  async function write() {
    if (!notesOk || writing) return;
    const mine = ++run.current;
    setWriting(true);
    setWriteError(null);
    try {
      const examples = user?.id ? await loadStyleExamples(user.id) : [];
      const v = latest.current;
      const text = await describePlace({
        place: {
          name: v.name,
          kind: lastPlace?.kind ?? null,
          city: lastPlace?.city ?? null,
          country: lastPlace?.country ?? null,
          id: lastPlace?.id,
          lat: v.latitude,
          lng: v.longitude,
        },
        notes: notes.trim(),
        examples,
      });
      if (mine !== run.current) return;
      setUndo({ text: v.description });
      onChange({ description: text });
      mark(['description']);
    } catch (e) {
      if (mine !== run.current) return;
      setWriteError(e instanceof Error ? e.message : 'We could not write it right now. Please try again.');
    }
    if (mine === run.current) setWriting(false);
  }

  function undoWrite() {
    if (!undo) return;
    onChange({ description: undo.text });
    setSuggested((s) => { const n = new Set(s); n.delete('description'); return n; });
    setUndo(null);
  }

  const picked = hasPin && !changing;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
      {/* Where */}
      <div className="flex flex-col gap-4 min-w-0">
        {picked ? (
          <div className="rounded-2xl border border-app-border bg-app-card overflow-hidden">
            <PlaceMap latitude={value.latitude!} longitude={value.longitude!} name={value.name} height={210} />
            <div className="flex items-center gap-3 px-4 py-3">
              <i className="ti ti-map-pin text-gold text-xl shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-app-fg truncate">{value.address || 'Pinned on the map'}</div>
                <div className="text-xs text-app-muted">Guests see this pin and can get directions</div>
              </div>
              <button
                type="button"
                onClick={() => setChanging(true)}
                className="shrink-0 min-h-[44px] px-4 rounded-full border border-app-border text-[13px] font-medium text-app-fg hover:bg-black/5"
              >
                Change place
              </button>
            </div>
          </div>
        ) : manual ? (
          <div className="flex flex-col gap-3">
            <AddressInput
              label="Address"
              value={value.address ?? ''}
              onChange={(v) => onChange({ address: v || null })}
              onGeocode={(lat, lng) => onChange({ latitude: lat, longitude: lng })}
              placeholder="Start typing the address…"
            />
            <button type="button" onClick={() => setManual(false)} className="self-start text-sm text-gold underline underline-offset-2">
              Search by name instead
            </button>
          </div>
        ) : (
          <>
            <PlaceFinder near={near} onPick={pick} onManual={() => setManual(true)} autoFocus={!changing} />
            {changing && (
              <button type="button" onClick={() => setChanging(false)} className="self-start text-sm text-app-muted underline underline-offset-2">
                Keep the current place
              </button>
            )}
          </>
        )}
        {!picked && !manual && !hasPin && (
          <p className="text-sm text-app-muted rounded-xl border border-dashed border-app-border px-4 py-3">
            Pick a place and we fill in the <strong className="font-medium text-app-fg-2">name, address, category and map</strong>.
            Then add your photos and a few words about it, and we write the description for you.
          </p>
        )}
      </div>

      {/* The rest */}
      <div className="flex flex-col gap-4 min-w-0">
        <div className="flex flex-col gap-2">{photos}</div>

        <div className="grid grid-cols-1 sm:grid-cols-[1.3fr_1fr] gap-3">
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2">
              <label htmlFor="place-name" className="text-sm font-semibold text-app-fg-2">Name</label>
              <Tag show={suggested.has('name')} />
            </div>
            <input id="place-name" value={value.name} onChange={(e) => edit('name', { name: e.target.value })}
              placeholder="Taberna Belga" className={inputClass} />
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2">
              <label htmlFor="place-category" className="text-sm font-semibold text-app-fg-2">Category</label>
              <Tag show={suggested.has('category')} />
            </div>
            <select id="place-category" value={value.category} onChange={(e) => edit('category', { category: e.target.value })}
              className={`${inputClass} appearance-none pr-9`}>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <label htmlFor="place-description" className="text-sm font-semibold text-app-fg-2">Description</label>
            <Tag show={suggested.has('description')} text="written for you" />
          </div>
          <textarea id="place-description" value={value.description} onChange={(e) => edit('description', { description: e.target.value })}
            rows={4} placeholder="What makes this place special: the vibe, the food, why you love it…"
            className={`${inputClass} resize-none`} />

          <div className="rounded-xl border border-app-border bg-app-bg/60 p-3 flex flex-col gap-2 mt-1">
            <label htmlFor="place-notes" className="text-sm font-medium text-app-fg-2">In a few words, what do you love about it?</label>
            <input
              id="place-notes"
              value={notes}
              maxLength={NOTES_MAX}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. tiny, Belgian beers, the owner remembers you"
              className={inputClass}
            />
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void write()}
                disabled={!notesOk || writing}
                className="min-h-[44px] px-4 rounded-full bg-gold text-ink text-sm font-semibold disabled:opacity-40"
              >
                {value.description.trim() ? 'Rewrite it for me' : 'Write it for me'}
              </button>
              {writing && <span className="inline-flex items-center gap-2 text-xs text-app-muted"><Spinner size="sm" /> Writing…</span>}
              {!writing && undo && (
                <button type="button" onClick={undoWrite} className="text-sm text-gold underline underline-offset-2">Undo</button>
              )}
            </div>
            <p className="text-xs text-app-muted" aria-live="polite">
              {writeError
                ? <span className="text-crimson">{writeError}</span>
                : 'We turn your words into two or three sentences, in your own style. Check them and make them yours.'}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="place-tip" className="text-sm font-semibold text-app-fg-2">Local tip</label>
          <textarea id="place-tip" value={value.tip ?? ''} onChange={(e) => onChange({ tip: e.target.value || null })}
            rows={2} placeholder="Ask for the house wine, sit upstairs for the view, go before 7pm…"
            className={`${inputClass} resize-none`} />
        </div>

        {extra}
      </div>
    </div>
  );
}

export default PlaceDetailsEditor;
